import { useState, useRef, useEffect } from "react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Slider } from "./ui/slider";
import { ZoomIn, ZoomOut, Move, Check, X } from "lucide-react";

export default function PrefixImageEditor({ isOpen, onClose, onSave, templateId }) {
  const [image, setImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Card dimensions: 2.25" x 3.25" at 300dpi
  // Bottom 1/4 = 2.25" x 0.8125"
  const PREVIEW_WIDTH = 450; // Preview width in pixels
  const PREVIEW_HEIGHT = 162; // 1/4 of card height proportionally
  const ASPECT_RATIO = 2.25 / 0.8125; // Width / Height

  useEffect(() => {
    if (image) {
      drawPreview();
    }
  }, [image, position, scale]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        setImage(img);
        // Center the image initially
        setPosition({ x: 0, y: 0 });
        // Calculate initial scale to fit
        const scaleX = PREVIEW_WIDTH / img.width;
        const scaleY = PREVIEW_HEIGHT / img.height;
        setScale(Math.max(scaleX, scaleY));
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  const drawPreview = () => {
    const canvas = canvasRef.current;
    if (!canvas || !image) return;

    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, PREVIEW_WIDTH, PREVIEW_HEIGHT);

    const imgWidth = image.width * scale;
    const imgHeight = image.height * scale;
    const x = (PREVIEW_WIDTH - imgWidth) / 2 + position.x;
    const y = (PREVIEW_HEIGHT - imgHeight) / 2 + position.y;

    ctx.drawImage(image, x, y, imgWidth, imgHeight);

    // Draw border
    ctx.strokeStyle = '#9333ea';
    ctx.lineWidth = 2;
    ctx.strokeRect(0, 0, PREVIEW_WIDTH, PREVIEW_HEIGHT);
  };

  const handleMouseDown = (e) => {
    if (!image) return;
    setIsDragging(true);
    const rect = canvasRef.current.getBoundingClientRect();
    setDragStart({
      x: e.clientX - rect.left - position.x,
      y: e.clientY - rect.top - position.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !image) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - dragStart.x;
    const y = e.clientY - rect.top - dragStart.y;
    setPosition({ x, y });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    if (!image) return;
    e.preventDefault();
    setIsDragging(true);
    const touch = e.touches[0];
    const rect = canvasRef.current.getBoundingClientRect();
    setDragStart({
      x: touch.clientX - rect.left - position.x,
      y: touch.clientY - rect.top - position.y
    });
  };

  const handleTouchMove = (e) => {
    if (!isDragging || !image) return;
    e.preventDefault();
    const touch = e.touches[0];
    const rect = canvasRef.current.getBoundingClientRect();
    const x = touch.clientX - rect.left - dragStart.x;
    const y = touch.clientY - rect.top - dragStart.y;
    setPosition({ x, y });
  };

  const handleSave = async () => {
    if (!image || !imageFile) return;

    // Create a canvas with the final cropped image at full resolution
    const outputCanvas = document.createElement('canvas');
    const outputWidth = 675; // 2.25" at 300dpi
    const outputHeight = 244; // ~0.8125" at 300dpi (1/4 of 975)
    outputCanvas.width = outputWidth;
    outputCanvas.height = outputHeight;

    const ctx = outputCanvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, outputWidth, outputHeight);

    // Scale the position and scale for the output resolution
    const scaleRatio = outputWidth / PREVIEW_WIDTH;
    const imgWidth = image.width * scale * scaleRatio;
    const imgHeight = image.height * scale * scaleRatio;
    const x = (outputWidth - imgWidth) / 2 + position.x * scaleRatio;
    const y = (outputHeight - imgHeight) / 2 + position.y * scaleRatio;

    ctx.drawImage(image, x, y, imgWidth, imgHeight);

    // Convert to blob and upload
    outputCanvas.toBlob(async (blob) => {
      if (!blob) return;
      
      const formData = new FormData();
      formData.append('file', blob, 'prefix_image.png');
      
      try {
        await onSave(formData);
        handleClose();
      } catch (error) {
        alert('Failed to save prefix image');
      }
    }, 'image/png');
  };

  const handleClose = () => {
    setImage(null);
    setImageFile(null);
    setPosition({ x: 0, y: 0 });
    setScale(1);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Upload Prefix/Logo Image</DialogTitle>
          <DialogDescription>
            This image appears at the bottom 1/4 of each branded card
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* File Input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
          
          {!image ? (
            <div 
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-purple-300 rounded-lg p-8 text-center cursor-pointer hover:border-purple-500 hover:bg-purple-50 transition-all"
            >
              <div className="text-4xl mb-2">📷</div>
              <p className="text-gray-600">Click to select image</p>
              <p className="text-sm text-gray-400 mt-1">PNG, JPG, or WEBP</p>
            </div>
          ) : (
            <>
              {/* Preview Canvas */}
              <div className="bg-gray-100 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-2 text-center">
                  <Move className="w-4 h-4 inline mr-1" />
                  Drag to position • Preview of bottom 1/4 area
                </p>
                <div 
                  ref={containerRef}
                  className="flex justify-center"
                >
                  <canvas
                    ref={canvasRef}
                    width={PREVIEW_WIDTH}
                    height={PREVIEW_HEIGHT}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleMouseUp}
                    className="border-2 border-purple-500 rounded cursor-move"
                    style={{ touchAction: 'none' }}
                  />
                </div>
              </div>

              {/* Zoom Control */}
              <div className="flex items-center gap-4 px-4">
                <ZoomOut className="w-5 h-5 text-gray-500" />
                <Slider
                  value={[scale * 100]}
                  onValueChange={(value) => setScale(value[0] / 100)}
                  min={10}
                  max={300}
                  step={5}
                  className="flex-1"
                />
                <ZoomIn className="w-5 h-5 text-gray-500" />
                <span className="text-sm text-gray-600 w-16">{Math.round(scale * 100)}%</span>
              </div>

              {/* Full Card Preview */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-2 text-center">Full card preview (2.25" × 3.25")</p>
                <div className="flex justify-center">
                  <div 
                    className="border-2 border-gray-300 rounded bg-white"
                    style={{ width: '135px', height: '195px' }}
                  >
                    {/* Top 3/4 - Photo area */}
                    <div 
                      className="bg-gray-200 flex items-center justify-center text-gray-400 text-xs"
                      style={{ height: '146px' }}
                    >
                      Guest Photo
                    </div>
                    {/* Bottom 1/4 - Prefix area */}
                    <div 
                      className="overflow-hidden"
                      style={{ height: '49px' }}
                    >
                      {image && (
                        <canvas
                          width={135}
                          height={49}
                          ref={(canvas) => {
                            if (canvas && image) {
                              const ctx = canvas.getContext('2d');
                              ctx.fillStyle = '#ffffff';
                              ctx.fillRect(0, 0, 135, 49);
                              const miniScale = 135 / PREVIEW_WIDTH;
                              const imgWidth = image.width * scale * miniScale;
                              const imgHeight = image.height * scale * miniScale;
                              const x = (135 - imgWidth) / 2 + position.x * miniScale;
                              const y = (49 - imgHeight) / 2 + position.y * miniScale;
                              ctx.drawImage(image, x, y, imgWidth, imgHeight);
                            }
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex-1"
                >
                  Choose Different Image
                </Button>
                <Button
                  onClick={handleSave}
                  className="flex-1 bg-purple-500 hover:bg-purple-600"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Save Prefix Image
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
